import { Link } from 'react-router-dom';
import { AlertTriangle, Shield, ArrowLeft, ExternalLink } from 'lucide-react';

export default function Legal() {
  return (
    <div className="min-h-screen bg-dark-900 text-dark-100">
      <div className="max-w-4xl mx-auto px-4 py-10 md:py-14 space-y-8">
        <header className="space-y-3">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <Link
              to="/login"
              className="inline-flex items-center gap-1.5 text-primary-400 hover:text-primary-300 transition-colors text-sm"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to login
            </Link>
            <a
              href="https://polymarket.com"
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1.5 text-dark-400 hover:text-primary-300 transition-colors text-sm"
            >
              Polymarket
              <ExternalLink className="w-4 h-4" />
            </a>
          </div>
          <h1 className="text-2xl md:text-3xl font-bold">Legal Notice, Terms of Use & Risk Disclosure</h1>
          <p className="text-sm md:text-base text-dark-300">
            This page explains the rules for using BetMimic (the “Service”) and the risks of trading prediction markets.
            If you do not agree, do not use the Service.
          </p>
        </header>

        <section className="rounded-2xl border border-red-500/30 bg-red-500/10 p-5 space-y-3">
          <div className="flex items-start gap-3">
            <div className="mt-0.5">
              <AlertTriangle className="w-5 h-5 text-red-300" />
            </div>
            <div className="space-y-2">
              <h2 className="text-base md:text-lg font-semibold text-red-100">Giant Risk Warning</h2>
              <ul className="list-disc list-inside text-sm text-red-100/90 space-y-1">
                <li>
                  Prediction markets and automated trading can result in <span className="font-semibold">rapid losses</span>,
                  including loss of your entire collateral.
                </li>
                <li>
                  Past performance of any wallet/trader is <span className="font-semibold">not</span> a guarantee of future results.
                </li>
                <li>
                  The Service is <span className="font-semibold">not</span> a broker, exchange, custodian, bank, investment adviser, or fiduciary.
                </li>
                <li>
                  You are solely responsible for deciding whether to trade and for complying with all laws that apply to you.
                </li>
              </ul>
            </div>
          </div>
        </section>

        <section className="space-y-4 rounded-2xl border border-dark-700 bg-dark-800/70 p-5">
          <h2 className="text-lg font-semibold flex items-center gap-2">
            <Shield className="w-5 h-5 text-primary-400" />
            Summary (plain English)
          </h2>
          <ul className="list-disc list-inside text-sm text-dark-200 space-y-2">
            <li>
              BetMimic is a software tool that helps you monitor wallets and optionally automate order placement via official APIs.
            </li>
            <li>
              You control your configurations. If you turn something on, it can trade on your behalf.
            </li>
            <li>
              The Service may have bugs, downtime, latency, incorrect data, missed events, and execution drift.
            </li>
            <li>
              You accept that you may lose money and that we are not liable for trading losses.
            </li>
          </ul>
        </section>

        <section className="space-y-4">
          <h2 className="text-lg font-semibold">1) No investment advice</h2>
          <p className="text-sm text-dark-300">
            BetMimic does not provide investment, legal, tax, or accounting advice. Any information displayed by the Service
            (including “copy signals”, statistics, P&L, win rates, market data, or risk controls) is for informational and
            operational purposes only.
          </p>
        </section>

        <section className="space-y-4">
          <h2 className="text-lg font-semibold">2) Non-custodial; you remain responsible</h2>
          <p className="text-sm text-dark-300">
            BetMimic is designed as a non-custodial tool. We do not take possession of your funds. You are solely responsible
            for safeguarding your wallet access, private keys, and credentials, and for the consequences of enabling automation.
          </p>
        </section>

        <section className="space-y-4">
          <h2 className="text-lg font-semibold">3) Risks of trading + automation</h2>
          <ul className="list-disc list-inside text-sm text-dark-300 space-y-2">
            <li>Market risk, liquidity risk, and sudden price moves.</li>
            <li>Slippage, partial fills, and inability to exit at expected prices.</li>
            <li>Latency and data delays causing execution at worse prices than the observed signal.</li>
            <li>Software bugs, misconfiguration, or unexpected interactions with external APIs/services.</li>
            <li>Smart contract, network, and infrastructure risks (including Polygon/network congestion).</li>
          </ul>
        </section>

        <section className="space-y-4">
          <h2 className="text-lg font-semibold">4) Third-party services</h2>
          <p className="text-sm text-dark-300">
            The Service integrates with third-party services (including Polymarket, RPC providers, and data APIs). We do not
            control these services and are not responsible for their outages, changes, or failures.
          </p>
        </section>

        <section className="space-y-4">
          <h2 className="text-lg font-semibold">5) No warranties</h2>
          <p className="text-sm text-dark-300">
            The Service is provided “AS IS” and “AS AVAILABLE”. We disclaim all warranties to the maximum extent permitted by law,
            including implied warranties of merchantability, fitness for a particular purpose, and non-infringement.
          </p>
        </section>

        <section className="space-y-4">
          <h2 className="text-lg font-semibold">6) Limitation of liability</h2>
          <p className="text-sm text-dark-300">
            To the maximum extent permitted by law, BetMimic and its operators will not be liable for any indirect, incidental,
            special, consequential, or punitive damages, or any loss of profits, revenue, data, goodwill, or trading losses arising
            out of or related to your use of the Service.
          </p>
        </section>

        <section className="space-y-4">
          <h2 className="text-lg font-semibold">7) Eligibility + compliance</h2>
          <p className="text-sm text-dark-300">
            You represent that you are legally permitted to use the Service under the laws applicable to you. You are solely
            responsible for compliance with all laws, regulations, and rules related to prediction markets, trading automation,
            and the use of third-party services.
          </p>
        </section>

        <section className="space-y-4">
          <h2 className="text-lg font-semibold">8) Prohibited use</h2>
          <ul className="list-disc list-inside text-sm text-dark-300 space-y-2">
            <li>Attempting to gain unauthorized access to accounts, systems, or keys.</li>
            <li>Abusing the Service, scraping, or interfering with infrastructure.</li>
            <li>Using the Service for unlawful activity.</li>
          </ul>
        </section>

        <section className="space-y-4">
          <h2 className="text-lg font-semibold">9) Changes</h2>
          <p className="text-sm text-dark-300">
            We may update these terms from time to time. Continued use after an update may require re-acceptance.
          </p>
        </section>

        <section className="rounded-2xl border border-dark-700 bg-dark-800/70 p-5 space-y-2">
          <h2 className="text-base font-semibold">Questions</h2>
          <p className="text-sm text-dark-300">
            If you have questions, contact support in Discord.
          </p>
          <a
            href="https://discord.gg/dFSefc9Hs9"
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-xl bg-primary-600/90 hover:bg-primary-500 text-sm font-medium text-white shadow-lg shadow-primary-600/40"
          >
            Open Discord
          </a>
        </section>
      </div>
    </div>
  );
}
